import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router";
import clsx from "clsx";
import {
  Button,
  makeStyles,
  Typography,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Theme,
  Dialog,
  DialogTitle,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl, ClickAwayListener, Box, CircularProgress,
} from "@material-ui/core";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import { BasketItem, SkiSchool } from "../../Types/Types";
import { find, flatten, get, keys, sum, values } from "lodash";

export const useStyles = makeStyles(
  (theme: Theme) => ({
    totalAmount: {
      fontWeight: "bold",
      paddingTop: 10,
    },
    activeButton: {
      textTransform: "none",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
      },
    },
    quantityUpdateButton: {
      width: "100%",
    },
    quantityUpdate: {
      padding: 32,
    },
    back: {
      textDecoration: "underline",
      marginBottom: 10,
      cursor: "pointer",
    },
    itemContainer: {
      display: "flex",
      minHeight: 260,
      marginTop: 16,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    content: {
      maxWidth: 940,
      flex: "0 0 auto",
    },
    information: {
      display: "flex",
      justifyContent: "space-between",
    },
    requestForm: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      [theme.breakpoints.down("xs")]: {
        minWidth: "100%",
        overflow: "hidden",
        marginLeft: "0 !important",
      },
    },
    itemInfo: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    itemActions: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: 16,
    },
    itemAction: {
      textTransform: "none",
      width: "50%",
      "&.active": {
        marginLeft: 10,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    amountData: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    },
    helperText: {
      display: "none",
    },
    itemImage: {
      width: "40%",
      height: "auto",
      marginRight: 60,
      flexShrink: 0,
      maxHeight: 260,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    textRoot: {
      alignItems: "flex-start",
      margin: "5px 0px",
      width: "100%",
      "& input,textarea,.inputSelect": {
        minWidth: 250,
        [theme.breakpoints.down("xs")]: {
          minWidth: "100%",
        },
      },
      [theme.breakpoints.down("xs")]: {
        "& .MuiFormControl-root,.MuiInputBase-root": {
          width: "100%",
        },
      },
      "& input,.inputSelect": {
        padding: "10.5px 14px",
      },
      "& fieldset": {
        borderRadius: 0,
      },
    },
    textLabel: {
      width: 300,
      maxWidth: 300,
    },
    actionButtons: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "flex-end",
      width: "100%",
    },
    actions: {
      maxWidth: 940,
      flexDirection: "column",
      display: "flex",
      alignItems: "flex-start",
      padding: "24px",
    },
    textFieldRoot: {
      marginRight: 20,
      [theme.breakpoints.down("xs")]: {
        marginRight: 0,
        marginBottom: 20,
      },
      "& input": {
        padding: "10.5px 14px",
      },
    },
    outlinedText: {
      transform: "translate(14px, 14px) scale(1)",
    },
    select: {
      padding: "10.5px 14px",
    },
    basketForm: {
      display: "flex",
      width: "100%",
      margin: "24px 0px",
      "& > div": {
        flex: "1 1 0",
      },
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
      },
    },
    retailerSelect: {
      transform: "translate(14px, 14px) scale(1)",
    },
    textField: {
      width: "100%",
      "& input": {
        padding: "10.5px 14px",
      },
    },
    menuList: {
      backgroundColor: "#fff",
      position: "absolute",
      maxHeight: 200,
      width: "100%",
      zIndex: 2,
      overflowX: "hidden",
      overflowY: "auto",
      // adding similar boxShadow as select field menulist
      boxShadow:
        "0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
    },
    filterField: {
      position: "relative",
    },
    menuItem: {
      display: "flex",
      padding: 10,
      marginTop: 5,
      cursor: "pointer",
    },
  }),
  { name: "Basket" }
);

export const countries: { value: string; label: string }[] = [
  { value: "germany", label: "Deutschland" },
  { value: "france", label: "Frankreich" },
  { value: "austria", label: "Österreich" },
  { value: "swiss", label: "Schweiz" },
  { value: "other", label: "Sonstiges" },
];

export interface BasketProps {
  componentWithSelectedOption: { component: string; option: string }[];
  isLoggedIn: boolean;
  selectedSkiSchool: SkiSchool;
  fetchRetailers: () => void;
  retailers: { id: string; companyName: string; customerNumber: string }[];
  basketItems: BasketItem[];
  deleteBasketItem: (id: number) => void;
  selectedItem: null | BasketItem;
  selectItem: (item: BasketItem) => void;
  handleAmountUpdate: () => void;
  updateBasket: (item: BasketItem) => void;
  sendMail: (data: any) => void;
  loadIdentifier: string | null;
  clearData: boolean;
  mailFailed: boolean;
  ProductVariantComponent: any;
}

const Basket = (props: BasketProps) => {
  const classes = useStyles();
  const history = useHistory();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [country, setCountry] = useState<string>(countries[0].value);
  const [info, setInfo] = useState<string>("");
  const [validate, setValidate] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [skischoolname, setSkischoolname] = useState<string>("");
  const [selectedRetailer, setSelectedRetailer] = useState<any>(null);
  const [selectedRetailerText, setSelectedRetailerText] = useState<any>("");
  const [showProductVariant, setShowProductVariant] = useState<boolean>(false);
  const [filteredRetailers, setFilteredRetailers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [showMailResponseError, setShowMailResponseError] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [disableBtn, setDiableBtn] = useState(false);

  const {
    isLoggedIn,
    selectedSkiSchool,
    fetchRetailers,
    updateBasket,
    handleAmountUpdate,
    retailers,
    basketItems,
    deleteBasketItem,
    selectedItem,
    selectItem,
    sendMail,
    loadIdentifier,
    clearData,
    mailFailed,
    ProductVariantComponent,
  } = props;

  useEffect(() => {
    if (!retailers || retailers.length === 0) {
      fetchRetailers();
    }
    showMailResponseError && setShowMailResponseError(false);
  }, []);

  useEffect(() => {
    if (validate && clearData && !mailFailed) {
      setName("");
      setInfo("");
      setEmail("");
      setSelectedRetailer(null);
      setSelectedRetailerText("");
      setValidate(false);
      setShowLoader(false);
      history.push({ pathname: "/thankyou", search: window.location.search });
    }
  }, [clearData]);

  useEffect(() => {
    if (mailFailed) {
      setShowMailResponseError(true);
      showLoader && setShowLoader(false);
    } else {
      setShowMailResponseError(false);
    }
  }, [mailFailed]);

  useEffect(() => {
    if (selectedSkiSchool) {
      setUsername(
        get(selectedSkiSchool, "user.firstname", "") +
          " " +
          get(selectedSkiSchool, "user.lastname", "")
      );
      setSkischoolname(selectedSkiSchool.name);
    }
  }, [selectedSkiSchool]);

  useEffect(() => {
    if (isTouched) {
      filterText ? filterRetailer(filterText) : setFilteredRetailers(retailers);
    }
  }, [isTouched]);

  const filterRetailer = (value) => {
    const hasValidSelectedRetailer = selectedRetailer
      ? retailers.find(
          (r) => r.companyName + "  (" + r.customerNumber + ")" === value
        )
      : true;
    !hasValidSelectedRetailer && setSelectedRetailer(null);
    setFilterText(value);
    setFilteredRetailers(
      value === ""
        ? retailers
        : retailers.filter(
            (ss) =>
              ss.customerNumber.toLowerCase().includes(value.toLowerCase()) ||
              ss.companyName.toLowerCase().includes(value.toLowerCase())
          )
    );
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const allItemAmounts = flatten(
    basketItems.map((item) =>
      values(get(item, "configuration.customdata.selectedAmounts"))
    )
  );
  const total = sum(allItemAmounts);

  const getAmountData = (item) => {
    const children = get(item, "configuration.item.itemGroup.children", []);
    const selectedAmounts = get(
      item,
      "configuration.customdata.selectedAmounts",
      {}
    );
    return children.map((i) => ({
      title: i.itemgroupentryTitle,
      identifier: i.identifier,
      amount: get(selectedAmounts, i.identifier, 0),
    }));
  };

  const onSendRequestMail = async () => {
    if (name !== "" && email !== "") {
      setShowLoader(true);
      await sendMail({
        country: get(
          countries.find((c) => c.value === country),
          "label",
          ""
        ),
        message: info,
        senderName: name,
        senderMail: email,
      });
    }
  };

  const onSendOrderMail = async () => {
    if (selectedRetailer !== null && username !== "") {
      setShowLoader(true);
      if(!showMailResponseError){
        setDiableBtn(true)
      }
      await sendMail({
        username: username,
        retailer: selectedRetailer,
        skischoolname: skischoolname,
      });
    }
  };

  const handleClickAway = () => {
    setIsTouched(false);
    filteredRetailers.length > 0 && setFilteredRetailers([]);
  }

  const amount = get(
    selectedItem,
    "configuration.customdata.selectedAmounts",
    0
  );

  return (
    <>
      {showLoader && (
          <Box
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 2000,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "rgba(255,255,255,0.3)",
              }}
          >
            <CircularProgress />
          </Box>
      )}
      <Dialog open={true} fullScreen={true}>
        <DialogTitle>
          {isLoggedIn ? t("basket.cartTitle") : t("basket.requestTitle")}
          <Typography variant="body2">
            {isLoggedIn ? t("basket.cartInfo") : t("basket.requestInfo")}
          </Typography>
          <Typography variant="body2" className={classes.totalAmount}>
            {t("basket.selection")} {total} {t("basket.article")}
          </Typography>
        </DialogTitle>
        <DialogContent
          classes={{
            root: classes.content,
          }}
        >
          {basketItems.map((item, i) => {
            const optionclassifications = get(
              item,
              "configuration.optionclassifications",
              []
            );
            const componentWithSelectedOption = optionclassifications
              .filter((oc) => (oc.selectedoptions !== null && oc.selectedoptions.length > 0))
              .map((c) => ({
                component: c.title,
                option: c.selectedoptions.map((o) => o.title).join(", "),
              }));
            const selectedAmounts = get(
              item,
              "configuration.customdata.selectedAmounts",
              0
            );
            const children = get(
              item,
              "configuration.item.itemGroup.children",
              []
            );
            const selectedAmountWithData = keys(selectedAmounts).map(
              (identifier) => find(children, (c) => c.identifier === identifier)
            );
            const itemWithAmount = selectedAmountWithData.map((i) => ({
              title: i.itemgroupentryTitle,
              amount: get(selectedAmounts, i.identifier, 0),
            }));
            const designData = values(
              get(item, "configuration.designdata", [])
            );
            const embroideryObjects = designData.filter(
              (design) =>
                design.designProductionMethodIdentifier === "embroidery" &&
                design.canvasData !== null &&
                design.canvasData.objects.length > 0
            );
            return (
              <div className={classes.itemContainer} key={i}>
                <img
                  className={classes.itemImage}
                  src={get(item, "configuration.images[0]")}
                />
                <div className={classes.itemInfo}>
                  <div>
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {get(item, "configuration.item.title")}
                    </Typography>
                    {componentWithSelectedOption.map((data, i) => (
                      <div className={classes.information} key={i}>
                        <Typography variant="body2">
                          {data.component}
                        </Typography>
                        <Typography variant="body2">{data.option}</Typography>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className={classes.information}>
                      <Typography variant="body2">
                        {t("basket.quantity")}
                      </Typography>
                      <div className={classes.amountData}>
                        {itemWithAmount.map((data, i) => (
                          <Typography key={i} variant="body2">
                            {data.amount + "x " + data.title}
                          </Typography>
                        ))}
                      </div>
                    </div>
                    <div className={classes.information}>
                      <Typography variant="body2">
                        {t("basket.total")}
                      </Typography>
                      <Typography variant="body2">
                        {sum(values(selectedAmounts))}
                      </Typography>
                    </div>
                    <div className={classes.information}>
                      <Typography variant="body2">
                        {t("basket.embroidery")}
                      </Typography>
                      <Typography variant="body2">
                        {embroideryObjects.length > 0
                          ? embroideryObjects.length
                          : t("basket.no")}
                      </Typography>
                    </div>
                    <div className={classes.itemActions}>
                      <Button
                        className={classes.itemAction}
                        variant="outlined"
                        onClick={() => deleteBasketItem(item.id)}
                      >
                        {t("basket.delete")}
                      </Button>
                      <Button
                        className={clsx(classes.itemAction, "active")}
                        variant="contained"
                        onClick={() => {
                          setShowProductVariant(true);
                          selectItem(item);
                        }}
                      >
                        {t("basket.quantityUpdate")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </DialogContent>
        <DialogActions classes={{ root: classes.actions }}>
          <Typography variant="body2">
            {isLoggedIn
              ? t("basket.basketDescription")
              : t("basket.requestDescription")}
          </Typography>
          {isLoggedIn ? (
            <div className={classes.basketForm}>
              <TextField
                classes={{
                  root: classes.textFieldRoot,
                }}
                InputLabelProps={{
                  classes: {
                    outlined: classes.outlinedText,
                  },
                }}
                variant="outlined"
                label={t("basket.skischoolname")}
                value={skischoolname}
                disabled={true}
              />
              <TextField
                classes={{
                  root: classes.textFieldRoot,
                }}
                InputLabelProps={{
                  classes: {
                    outlined: classes.outlinedText,
                  },
                }}
                error={validate && username === ""}
                helperText={
                  validate && username === ""
                    ? t("basket.senderNameError")
                    : null
                }
                onChange={(e) => setUsername(e.target.value)}
                variant="outlined"
                label={t("basket.username")}
                value={username}
                disabled={true}
              />
              <FormControl>
                <ClickAwayListener onClickAway={handleClickAway}>
                  <div
                      className={classes.filterField}
                  >
                    <TextField
                        value={selectedRetailerText}
                        className={classes.textField}
                        variant="outlined"
                        label={t("basket.retailer")}
                        InputLabelProps={{
                          classes: {
                            outlined: classes.outlinedText,
                          },
                        }}
                        FormHelperTextProps={{
                          className:
                              validate &&
                              selectedRetailer === null &&
                              !isTouched &&
                              filteredRetailers.length === 0
                                  ? ""
                                  : classes.helperText,
                        }}
                        helperText={
                          validate &&
                          selectedRetailer === null &&
                          !isTouched &&
                          filteredRetailers.length === 0
                              ? t("basket.senderNameError")
                              : " "
                        }
                        onFocus={() => setIsTouched(true)}
                        onChange={(e) => {
                          setSelectedRetailerText(e.target.value);
                          filterRetailer(e.target.value);
                        }}
                        error={validate && selectedRetailer === null}
                    />
                    {(filteredRetailers.length > 0 || filterText !== "") && (
                        <div className={classes.menuList}>
                          {filteredRetailers.map((retailer) => (
                              <Typography
                                  variant="body1"
                                  className={classes.menuItem}
                                  onMouseDown={(e) => {
                                    const selected = retailers.find(
                                        (r) =>
                                            r.customerNumber === retailer.customerNumber
                                    );
                                    setSelectedRetailer(selected);
                                    setSelectedRetailerText(
                                        selected.companyName +
                                        "  (" +
                                        selected.customerNumber +
                                        ")"
                                    );
                                    setFilteredRetailers([]);
                                    setFilterText("");
                                  }}
                              >
                                {retailer.companyName +
                                "  (" +
                                retailer.customerNumber +
                                ")"}
                              </Typography>
                          ))}
                          {filterText !== "" &&
                          filteredRetailers.length === 0 &&
                          isTouched && (
                              <Typography
                                  variant="body1"
                                  style={{ cursor: "text" }}
                                  className={classes.menuItem}
                              >
                                {t("skiSchool.noEntries")}
                              </Typography>
                          )}
                        </div>
                    )}
                  </div>
                </ClickAwayListener>
              </FormControl>
            </div>
          ) : (
            <div className={classes.requestForm}>
              <FormControlLabel
                classes={{
                  root: classes.textRoot,
                  label: classes.textLabel,
                }}
                control={
                  <TextField
                    variant="outlined"
                    value={name}
                    placeholder={t("basket.senderNamePlaceholder")}
                    error={validate && name === ""}
                    FormHelperTextProps={{
                      className:
                        validate && name === "" ? "" : classes.helperText,
                    }}
                    helperText={
                      validate && name === ""
                        ? t("basket.senderNameError")
                        : " "
                    }
                    onChange={(e) => setName(e.target.value)}
                  />
                }
                label={t("basket.senderName")}
                labelPlacement={isSmallScreen ? "top" : "start"}
              />
              <FormControlLabel
                classes={{
                  root: classes.textRoot,
                  label: classes.textLabel,
                }}
                control={
                  <TextField
                    value={email}
                    variant="outlined"
                    placeholder={t("basket.senderMailPlaceholder")}
                    error={validate && email === ""}
                    FormHelperTextProps={{
                      className:
                        validate && email === "" ? "" : classes.helperText,
                    }}
                    helperText={
                      validate && email === ""
                        ? t("basket.senderMailError")
                        : " "
                    }
                    onChange={(e) => setEmail(e.target.value)}
                  />
                }
                label={t("basket.senderMail")}
                labelPlacement={isSmallScreen ? "top" : "start"}
              />
              <FormControlLabel
                classes={{
                  root: classes.textRoot,
                  label: classes.textLabel,
                }}
                control={
                  <Select
                    classes={{
                      root: "inputSelect",
                    }}
                    value={country}
                    variant="outlined"
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setCountry(e.target.value)
                    }
                  >
                    {countries.map((country) => (
                      <MenuItem value={country.value}>{country.label}</MenuItem>
                    ))}
                  </Select>
                }
                label={t("basket.country")}
                labelPlacement={isSmallScreen ? "top" : "start"}
              />
              <FormControlLabel
                classes={{
                  root: classes.textRoot,
                  label: classes.textLabel,
                }}
                control={
                  <TextField
                    placeholder={t("basket.moreInfoPlaceholder")}
                    value={info}
                    variant="outlined"
                    multiline={true}
                    rows={4}
                    onChange={(e) => setInfo(e.target.value)}
                  />
                }
                label={t("basket.moreInfo")}
                labelPlacement={isSmallScreen ? "top" : "start"}
              />
            </div>
          )}
          <div className={classes.actionButtons}>
            <Typography
              variant="body2"
              className={classes.back}
              onClick={() =>
                !loadIdentifier
                  ? history.push({
                      pathname: "/products/overview",
                      search: window.location.search,
                    })
                  : history.goBack()
              }
            >
              {t("basket.goBack")}
            </Typography>
            <Button
              disabled={(total > 0 ? false : true) || disableBtn}
              className={classes.activeButton}
              variant="contained"
              onClick={() => {
                setValidate(true);
                isLoggedIn ? onSendOrderMail() : onSendRequestMail();
              }}
            >
              {isLoggedIn ? t("basket.cart") : t("basket.inquiry")}
            </Button>
            {showMailResponseError && (
              <Typography color="error">{t("basketMail.error")}</Typography>
            )}
          </div>
        </DialogActions>
      </Dialog>
      {showProductVariant && (
        <CustomDialog
          open={showProductVariant}
          dialogTitle={t("basket.chooseQuantity")}
          fullScreen={isSmallScreen}
          onCancel={() => setShowProductVariant(false)}
        >
          <div className={classes.quantityUpdate}>
            <ProductVariantComponent
              title={get(selectedItem, "configuration.item.title", "")}
              amountMap={get(
                selectedItem,
                "configuration.customdata.selectedAmounts",
                {}
              )}
              amountData={getAmountData(selectedItem)}
              image={get(selectedItem, "configuration.images[0]")}
              handleChange={handleAmountUpdate}
            />
          </div>
              <DialogActions>
              <Button
                className={clsx(
                  classes.quantityUpdateButton,
                  classes.activeButton
                )}
                variant="contained"
                onClick={() => {
                  updateBasket(selectedItem);
                  setShowProductVariant(false);
                  selectItem(null);
                  if(sum(values(amount)) == 0){
                    deleteBasketItem(selectedItem.id);
                  }
                }}
              >
                {t("basket.confirmUpdate")}
              </Button>
            </DialogActions>
        </CustomDialog>
      )}
    </>
  );
};

export default Basket;
